import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Form, Modal, Alert, ProgressBar, Tabs, Tab, Spinner } from 'react-bootstrap';
import { Share, Send, BarChart, Gear, Plus, Play, Pause, Eye, Pencil, Trash, ArrowClockwise } from 'react-bootstrap-icons';
import { useAccount } from '../contexts/AccountContext';
import EMMSectionHeader from './EMMSectionHeader';
import HelpSidebar from './HelpSidebar';

const CrossIdentityCampaignDistribution = () => {
  const { selectedAccount, isAllAccountsSelected, isLoading } = useAccount();
  const [campaigns, setCampaigns] = useState([]);
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState('campaigns');
  const [showHelpSidebar, setShowHelpSidebar] = useState(false);

  const [newCampaign, setNewCampaign] = useState({
    name: '',
    subject: '',
    content: '',
    distributionStrategy: 'round-robin', // round-robin, load-balanced, health-based, manual
    selectedIdentities: [],
    sendSchedule: 'immediate', // immediate, scheduled
    scheduledTime: '',
    volumeLimit: 1000,
    rateLimit: 100, // emails per hour
    priority: 'normal' // low, normal, high
  });

  // No mock data - show empty state until real API is connected
  useEffect(() => {
    setLoading(true); // Show loading when account changes
    
    // Simulate loading delay
    const timer = setTimeout(() => {
      // No mock data - show empty state until real API is connected
      setIdentities([]);
      setCampaigns([]);
      setLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [selectedAccount.id, isAllAccountsSelected]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">Active</Badge>;
      case 'scheduled':
        return <Badge bg="info">Scheduled</Badge>;
      case 'completed':
        return <Badge bg="secondary">Completed</Badge>;
      case 'paused':
        return <Badge bg="warning">Paused</Badge>;
      case 'failed':
        return <Badge bg="danger">Failed</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return <Badge bg="danger">High</Badge>;
      case 'normal':
        return <Badge bg="primary">Normal</Badge>;
      case 'low':
        return <Badge bg="secondary">Low</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getStrategyBadge = (strategy) => {
    switch (strategy) {
      case 'round-robin':
        return <Badge bg="primary">Round Robin</Badge>;
      case 'load-balanced':
        return <Badge bg="info">Load Balanced</Badge>;
      case 'health-based':
        return <Badge bg="success">Health Based</Badge>;
      case 'manual':
        return <Badge bg="warning">Manual</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getProgressPercentage = (campaign) => {
    if (campaign.totalRecipients === 0) return 0;
    return Math.round((campaign.sentCount / campaign.totalRecipients) * 100);
  };

  const handleCreateCampaign = () => {
    setLoading(true);
    // TODO: Implement campaign creation
    setTimeout(() => {
      const newCampaignData = {
        id: campaigns.length + 1,
        ...newCampaign,
        status: newCampaign.sendSchedule === 'immediate' ? 'active' : 'scheduled',
        totalRecipients: 0, // Will be calculated based on subscriber lists
        sentCount: 0,
        remainingCount: 0,
        startTime: newCampaign.sendSchedule === 'immediate' ? new Date().toISOString() : newCampaign.scheduledTime,
        estimatedCompletion: null,
        openRate: null,
        clickRate: null,
        bounceRate: null
      };
      setCampaigns([...campaigns, newCampaignData]);
      setLoading(false);
      setShowCreateModal(false);
      setNewCampaign({
        name: '',
        subject: '',
        content: '',
        distributionStrategy: 'round-robin',
        selectedIdentities: [],
        sendSchedule: 'immediate',
        scheduledTime: '',
        volumeLimit: 1000,
        rateLimit: 100,
        priority: 'normal'
      });
    }, 2000);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setShowEditModal(true);
  };

  const handleToggleCampaign = (campaignId) => {
    setCampaigns(prev => 
      prev.map(campaign => 
        campaign.id === campaignId 
          ? { 
              ...campaign, 
              status: campaign.status === 'active' ? 'paused' : 'active'
            }
          : campaign
      )
    );
  };

  const handleDeleteCampaign = (campaignId) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      setCampaigns(prev => prev.filter(campaign => campaign.id !== campaignId));
    }
  };

  const handleViewAnalytics = (campaignId) => {
    // TODO: Implement analytics view
    console.log('View analytics for campaign:', campaignId);
  };

  // Show loading spinner while data is loading
  if (loading) {
    return (
      <Container className="py-4" style={{ position: 'relative', minHeight: '400px' }}>
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}
        >
          <div className="text-center">
            <ArrowClockwise size={32} className="text-primary mb-2" style={{ animation: 'spin 1s linear infinite' }} />
            <div className="text-muted">Loading campaign data...</div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <>
      <EMMSectionHeader
        title="Cross-Identity Campaign Distribution"
        showHelp
        onHelpClick={() => setShowHelpSidebar(true)}
      />
      <Container className="py-3">
      {/* Action bar - same spot and size as Clients "+ Client" button */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <Button
          variant="primary"
          size="sm"
          className="d-flex align-items-center"
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          onClick={() => setShowCreateModal(true)}
        >
          <Plus className="me-1" size={14} />
          Campaign
        </Button>
        <span className="text-muted" style={{ fontSize: '0.75rem' }}>{campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}</span>
      </div>
      <Row className="mb-4">
        <Col>
          <p className="text-muted mb-0">Distribute campaigns across multiple AWS identities for optimal deliverability</p>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-success">Active Campaigns</h5>
              <h3>{campaigns.filter(c => c.status === 'active').length}</h3>
              <small className="text-muted">Running</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-info">Scheduled</h5>
              <h3>{campaigns.filter(c => c.status === 'scheduled').length}</h3>
              <small className="text-muted">Pending</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-primary">Total Sent</h5>
              <h3>{campaigns.reduce((sum, c) => sum + c.sentCount, 0).toLocaleString()}</h3>
              <small className="text-muted">Emails</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-warning">Avg Open Rate</h5>
              <h3>
                {campaigns.filter(c => c.openRate !== null).length > 0 
                  ? Math.round(campaigns.filter(c => c.openRate !== null).reduce((sum, c) => sum + c.openRate, 0) / campaigns.filter(c => c.openRate !== null).length)
                  : 0}%
              </h3>
              <small className="text-muted">Performance</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="campaigns" title="Campaigns">
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Campaign Distribution ({campaigns.length})</h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Campaign Name</th>
                        <th>Status</th>
                        <th>Strategy</th>
                        <th>Identities</th>
                        <th>Progress</th>
                        <th>Performance</th>
                        <th>Priority</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign) => (
                        <tr key={campaign.id}>
                          <td>
                            <div>
                              <strong>{campaign.name}</strong>
                              <div className="text-muted small">{campaign.subject}</div>
                            </div>
                          </td>
                          <td>{getStatusBadge(campaign.status)}</td>
                          <td>{getStrategyBadge(campaign.distributionStrategy)}</td>
                          <td>
                            <Badge bg="info">
                              {campaign.selectedIdentities.length} identities
                            </Badge>
                          </td>
                          <td>
                            <div>
                              <div className="d-flex justify-content-between small mb-1">
                                <span>{campaign.sentCount.toLocaleString()}</span>
                                <span>{campaign.totalRecipients.toLocaleString()}</span>
                              </div>
                              <ProgressBar
                                now={getProgressPercentage(campaign)}
                                variant={getProgressPercentage(campaign) === 100 ? 'success' : 'primary'}
                                style={{ height: '6px' }}
                              />
                            </div>
                          </td>
                          <td>
                            {campaign.openRate !== null ? (
                              <div className="small">
                                <div>Open: {campaign.openRate}%</div>
                                <div>Click: {campaign.clickRate}%</div>
                                <div>Bounce: {campaign.bounceRate}%</div>
                              </div>
                            ) : (
                              <span className="text-muted">No data</span>
                            )}
                          </td>
                          <td>{getPriorityBadge(campaign.priority)}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                size="sm"
                                variant={campaign.status === 'active' ? 'outline-warning' : 'outline-success'}
                                onClick={() => handleToggleCampaign(campaign.id)}
                              >
                                {campaign.status === 'active' ? <Pause /> : <Play />}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleViewAnalytics(campaign.id)}
                              >
                                <BarChart />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => handleEditCampaign(campaign)}
                              >
                                <Pencil />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDeleteCampaign(campaign.id)}
                              >
                                <Trash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
        <Tab eventKey="identities" title="Identity Performance">
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Identity Performance Overview</h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Identity</th>
                        <th>Health Score</th>
                        <th>Status</th>
                        <th>Usage</th>
                        <th>Active Campaigns</th>
                        <th>Performance</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {identities.map((identity) => (
                        <tr key={identity.id}>
                          <td>
                            <div>
                              <strong>{identity.name}</strong>
                              <div className="text-muted small">{identity.domain}</div>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <ProgressBar
                                now={identity.healthScore}
                                variant={identity.healthScore >= 90 ? 'success' : identity.healthScore >= 70 ? 'warning' : 'danger'}
                                style={{ width: '60px', height: '8px' }}
                                className="me-2"
                              />
                              <span className="fw-bold">{identity.healthScore}%</span>
                            </div>
                          </td>
                          <td>
                            <Badge bg={identity.status === 'active' ? 'success' : identity.status === 'warning' ? 'warning' : 'danger'}>
                              {identity.status}
                            </Badge>
                          </td>
                          <td>
                            <div>
                              <div className="small">
                                {identity.usedLimit.toLocaleString()} / {identity.monthlyLimit.toLocaleString()}
                              </div>
                              <ProgressBar
                                now={(identity.usedLimit / identity.monthlyLimit) * 100}
                                variant="info"
                                style={{ height: '4px' }}
                              />
                            </div>
                          </td>
                          <td>
                            <Badge bg="primary">
                              {campaigns.filter(c => c.selectedIdentities.includes(identity.id) && c.status === 'active').length}
                            </Badge>
                          </td>
                          <td>
                            <div className="small text-muted">No recent data</div>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button size="sm" variant="outline-primary">
                                <Eye />
                              </Button>
                              <Button size="sm" variant="outline-secondary">
                                <Gear />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
      </Tabs>

      {/* Create Campaign Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Cross-Identity Campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Campaign Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Q1 Product Launch"
                    value={newCampaign.name}
                    onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Distribution Strategy</Form.Label>
                  <Form.Select
                    value={newCampaign.distributionStrategy}
                    onChange={(e) => setNewCampaign({...newCampaign, distributionStrategy: e.target.value})}
                  >
                    <option value="round-robin">Round Robin</option>
                    <option value="load-balanced">Load Balanced</option>
                    <option value="health-based">Health Based</option>
                    <option value="manual">Manual</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Subject Line</Form.Label>
              <Form.Control
                type="text"
                placeholder="Introducing Our New Product Line"
                value={newCampaign.subject}
                onChange={(e) => setNewCampaign({...newCampaign, subject: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter your email content here..."
                value={newCampaign.content}
                onChange={(e) => setNewCampaign({...newCampaign, content: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Select Identities</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {identities.map(identity => (
                  <Form.Check
                    key={identity.id}
                    type="checkbox"
                    id={`identity-${identity.id}`}
                    label={`${identity.name} (${identity.healthScore}%)`}
                    checked={newCampaign.selectedIdentities.includes(identity.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewCampaign({
                          ...newCampaign,
                          selectedIdentities: [...newCampaign.selectedIdentities, identity.id]
                        });
                      } else {
                        setNewCampaign({
                          ...newCampaign,
                          selectedIdentities: newCampaign.selectedIdentities.filter(id => id !== identity.id)
                        });
                      }
                    }}
                  />
                ))}
              </div>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Send Schedule</Form.Label>
                  <Form.Select
                    value={newCampaign.sendSchedule}
                    onChange={(e) => setNewCampaign({...newCampaign, sendSchedule: e.target.value})}
                  >
                    <option value="immediate">Send Immediately</option>
                    <option value="scheduled">Schedule for Later</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    value={newCampaign.priority}
                    onChange={(e) => setNewCampaign({...newCampaign, priority: e.target.value})}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {newCampaign.sendSchedule === 'scheduled' && (
              <Form.Group className="mb-3">
                <Form.Label>Scheduled Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={newCampaign.scheduledTime}
                  onChange={(e) => setNewCampaign({...newCampaign, scheduledTime: e.target.value})}
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateCampaign} disabled={loading}>
            {loading ? 'Creating...' : 'Create Campaign'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Help Sidebar */}
      <HelpSidebar 
        pageKey="cross-identity"
        isOpen={showHelpSidebar}
        onToggle={() => setShowHelpSidebar(!showHelpSidebar)}
      />
    </Container>
    </>
  );
};

export default CrossIdentityCampaignDistribution;
